import type { ReactElement } from 'react';
import { useState } from 'react';
import {
  Save,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  Mail,
  Database,
} from 'lucide-react';

interface SystemConfig {
  companyName: string;
  contactEmail: string;
  timeZone: string;
  emailNotifications: boolean;
  weeklyReports: boolean;
  backupFrequency: string;
  twoFactorAuth: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
}

const SystemSettings = (): ReactElement => {
  const [config, setConfig] = useState<SystemConfig>({
    companyName: 'Bomuca',
    contactEmail: 'contacto@bomuca.com',
    timeZone: 'America/Mexico_City',
    emailNotifications: true,
    weeklyReports: true,
    backupFrequency: 'daily',
    twoFactorAuth: false,
    sessionTimeout: 120,
    maxLoginAttempts: 3,
  });

  const [saved, setSaved] = useState(false);

  const handleSave = (): void => {
    // Aquí iría la lógica para guardar en la API
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleConfigChange = (
    key: keyof SystemConfig,
    value: string | number | boolean
  ): void => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const logs = [
    {
      time: '2025-01-15 10:30:15',
      message: 'Usuario admin@bomuca.com inició sesión',
      type: 'info',
    },
    {
      time: '2025-01-15 09:45:32',
      message: 'Nuevo punto de venta creado: Aurrerá Express',
      type: 'success',
    },
    {
      time: '2025-01-15 08:20:11',
      message: 'Reporte generado por Eric Nicacio Castillo',
      type: 'info',
    },
    {
      time: '2025-01-14 16:15:44',
      message: 'Error de conexión con base de datos',
      type: 'error',
    },
    {
      time: '2025-01-14 14:22:33',
      message: 'Backup automático completado',
      type: 'success',
    },
    {
      time: '2025-01-14 12:10:22',
      message: 'Nueva cadena agregada: Farmacia Guadalajara',
      type: 'success',
    },
    {
      time: '2025-01-14 10:05:15',
      message: 'Usuario maría@bomuca.com cambió contraseña',
      type: 'info',
    },
    {
      time: '2025-01-13 18:30:44',
      message: 'Intento de acceso fallido desde IP 192.168.1.100',
      type: 'warning',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Configuración del Sistema
          </h2>
          <p className="text-sm text-gray-600">
            Configurar parámetros generales del sistema
          </p>
        </div>
        {saved && (
          <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">Configuración guardada</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Configuración General */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-lg text-gray-900">
              Configuración General
            </h3>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la Empresa
              </label>
              <input
                type="text"
                value={config.companyName}
                onChange={(e) =>
                  handleConfigChange('companyName', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email de Contacto
              </label>
              <input
                type="email"
                value={config.contactEmail}
                onChange={(e) =>
                  handleConfigChange('contactEmail', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zona Horaria
              </label>
              <select
                value={config.timeZone}
                onChange={(e) => handleConfigChange('timeZone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
              >
                <option value="America/Mexico_City">America/Mexico_City</option>
                <option value="America/New_York">America/New_York</option>
                <option value="America/Los_Angeles">America/Los_Angeles</option>
                <option value="Europe/Madrid">Europe/Madrid</option>
              </select>
            </div>
          </div>
        </div>

        {/* Configuración de Reportes */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-lg text-gray-900">
              Configuración de Reportes
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div>
                <p className="font-medium text-gray-900">
                  Notificaciones por Email
                </p>
                <p className="text-sm text-gray-600">
                  Enviar reportes automáticamente
                </p>
              </div>
              <input
                type="checkbox"
                checked={config.emailNotifications}
                onChange={(e) =>
                  handleConfigChange('emailNotifications', e.target.checked)
                }
                className="h-4 w-4 text-primary-dark focus:ring-primary-medium border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div>
                <p className="font-medium text-gray-900">Reportes Semanales</p>
                <p className="text-sm text-gray-600">
                  Resumen semanal de actividad
                </p>
              </div>
              <input
                type="checkbox"
                checked={config.weeklyReports}
                onChange={(e) =>
                  handleConfigChange('weeklyReports', e.target.checked)
                }
                className="h-4 w-4 text-primary-dark focus:ring-primary-medium border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Frecuencia de Backup
              </label>
              <select
                value={config.backupFrequency}
                onChange={(e) =>
                  handleConfigChange('backupFrequency', e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
              >
                <option value="daily">Diario</option>
                <option value="weekly">Semanal</option>
                <option value="monthly">Mensual</option>
              </select>
            </div>
          </div>
        </div>

        {/* Configuración de Seguridad */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-lg text-gray-900">
              Configuración de Seguridad
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div>
                <p className="font-medium text-gray-900">
                  Autenticación de Dos Factores
                </p>
                <p className="text-sm text-gray-600">
                  Requerir 2FA para administradores
                </p>
              </div>
              <input
                type="checkbox"
                checked={config.twoFactorAuth}
                onChange={(e) =>
                  handleConfigChange('twoFactorAuth', e.target.checked)
                }
                className="h-4 w-4 text-primary-dark focus:ring-primary-medium border-gray-300 rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Tiempo de Sesión (minutos)
              </label>
              <input
                type="number"
                value={config.sessionTimeout}
                onChange={(e) =>
                  handleConfigChange('sessionTimeout', parseInt(e.target.value))
                }
                min="5"
                max="480"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                Intentos de Login Fallidos
              </label>
              <input
                type="number"
                value={config.maxLoginAttempts}
                onChange={(e) =>
                  handleConfigChange(
                    'maxLoginAttempts',
                    parseInt(e.target.value)
                  )
                }
                min="1"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-medium focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Logs del Sistema */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-4 text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-gray-600" />
            Logs del Sistema
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {logs.map((log, index) => (
              <div
                key={index}
                className={`text-xs p-2 rounded border-l-4 ${
                  log.type === 'error'
                    ? 'bg-red-50 border-red-400 text-red-700'
                    : log.type === 'success'
                    ? 'bg-green-50 border-green-400 text-green-700'
                    : log.type === 'warning'
                    ? 'bg-yellow-50 border-yellow-400 text-yellow-700'
                    : 'bg-gray-50 border-gray-400 text-gray-700'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="font-mono text-gray-500 text-xs whitespace-nowrap">
                    {log.time}
                  </span>
                  <span className="flex-1">{log.message}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      log.type === 'error'
                        ? 'bg-red-100 text-red-600'
                        : log.type === 'success'
                        ? 'bg-green-100 text-green-600'
                        : log.type === 'warning'
                        ? 'bg-yellow-100 text-yellow-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {log.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <button className="text-sm text-primary-dark hover:text-primary-medium">
              Ver todos los logs →
            </button>
          </div>
        </div>
      </div>

      {/* Acciones */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => {
            setConfig({
              companyName: 'Bomuca',
              contactEmail: 'contacto@bomuca.com',
              timeZone: 'America/Mexico_City',
              emailNotifications: true,
              weeklyReports: true,
              backupFrequency: 'daily',
              twoFactorAuth: false,
              sessionTimeout: 120,
              maxLoginAttempts: 3,
            });
          }}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Restablecer
        </button>
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-primary-dark text-white px-6 py-2 rounded-lg hover:bg-primary-medium transition-colors font-medium"
        >
          <Save className="w-4 h-4" />
          Guardar Configuración
        </button>
      </div>
    </div>
  );
};

export default SystemSettings;
